import * as React from 'react'
import { Link, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { Cell, DataRow, HeaderRow, InlineDataTable, TableHeader } from 'uiComponents/table'
import { TooltipWrapper } from 'uiComponents/tooltip.new/tooltipWrapper'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { Reveal } from 'uiComponents/reveal/reveal'
import { quote } from 'capacity/copy'
import { capacityPaths } from 'capacity/paths'
import { limitText } from 'capacity/staging/savePlan'
import { CapacityListRow } from '../model/listModel'
import { useTranslation } from 'translations'

const COLUMN_WIDTHS = [null, '9rem', '9rem', '9rem']
const SKELETON_ROWS = [0, 1, 2]
const CELL_CLASS = 'capacity-list-cell wrapContent overflow-allowed'

interface RowProps {
    accountSlug: string
    row: CapacityListRow
    countsFailed: boolean
}

function CapacityRow({ accountSlug, row, countsFailed }: RowProps) {
    const { t } = useTranslation()
    const history = useHistory()
    const href = capacityPaths.item(accountSlug, row.id)
    const total = (row.productsCount ?? 0) + (row.optionItemsCount ?? 0)
    return (
        <DataRow
            role="row"
            className={classNames('capacity-list-row', { 'is-muted': row.productsCount !== null && total === 0 })}
            onClick={() => history.push(href)}
        >
            <Cell role="cell" className={classNames(CELL_CLASS, 'capacity-list-name-cell')}>
                <Link to={href} className="capacity-list-name" onClick={(event) => event.stopPropagation()}>
                    {row.name}
                </Link>
            </Cell>
            <Cell role="cell" className={CELL_CLASS}>
                {limitText(row.limit)}
            </Cell>
            <Cell role="cell" className={CELL_CLASS}>
                <TooltipWrapper
                    text={t(`capacity.list.countsRowTooltip.${row.countingType}`)}
                    arrowPosition="right"
                    className="capacity-tip capacity-tip-right"
                    revealOnFocus
                >
                    <span tabIndex={0} className="capacity-list-counts" onClick={(event) => event.stopPropagation()}>
                        {t(`capacity.config.countsLabel.${row.countingType}`)}
                    </span>
                </TooltipWrapper>
            </Cell>
            <Cell role="cell" className={CELL_CLASS}>
                {row.productsCount !== null ? (
                    row.optionItemsCount ?? 0 > 0 ? (
                        t('capacity.list.productsWithOptions', {
                            products: row.productsCount,
                            count: row.optionItemsCount ?? 0,
                        })
                    ) : (
                        `${row.productsCount}`
                    )
                ) : countsFailed ? (
                    <span className="capacity-list-not-loaded">{t('capacity.list.notLoaded')}</span>
                ) : (
                    <SkeletonBar width="4.5rem" height="0.85rem" className="capacity-list-skeleton-bar" />
                )}
            </Cell>
        </DataRow>
    )
}

function SkeletonRow() {
    return (
        <DataRow role="row" className="capacity-list-row capacity-list-row-skeleton" aria-hidden="true">
            <Cell role="cell" className={CELL_CLASS}>
                <SkeletonBar width="14rem" height="0.95rem" className="capacity-list-skeleton-bar" />
            </Cell>
            {[0, 1, 2].map((index) => (
                <Cell key={index} role="cell" className={CELL_CLASS}>
                    <SkeletonBar width="4.5rem" height="0.85rem" className="capacity-list-skeleton-bar" />
                </Cell>
            ))}
        </DataRow>
    )
}

interface CapacitiesTableProps {
    accountSlug: string
    rows: CapacityListRow[]
    loading: boolean
    error: boolean
    onRetry: () => void
    countsFailed: boolean
    onRetryCounts: () => void
    empty: boolean
    query: string
    onClearSearch: () => void
}

export function CapacitiesTable({
    accountSlug,
    rows,
    loading,
    error,
    onRetry,
    countsFailed,
    onRetryCounts,
    empty,
    query,
    onClearSearch,
}: CapacitiesTableProps) {
    const { t } = useTranslation()
    const noMatch = !loading && !error && !empty && rows.length === 0
    return (
        <div className="capacity-table-card capacity-list-card">
            <InlineDataTable bordered role="table" className="capacity-table" columnWidths={COLUMN_WIDTHS}>
                <HeaderRow role="row" className="capacity-table-head">
                    <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                        {t('capacity.list.columns.name')}
                    </TableHeader>
                    <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                        {t('capacity.list.columns.limit')}
                    </TableHeader>
                    <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                        <TooltipWrapper
                            text={t('capacity.list.countsTooltip')}
                            arrowPosition="right"
                            className="capacity-tip capacity-header-tip capacity-tip-right"
                            revealOnFocus
                        >
                            <span tabIndex={0}>{t('capacity.list.columns.counts')}</span>
                        </TooltipWrapper>
                    </TableHeader>
                    <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                        {t('capacity.list.columns.products')}
                    </TableHeader>
                </HeaderRow>
                <Reveal open={loading}>
                    <div role="rowgroup">
                        {SKELETON_ROWS.map((index) => (
                            <SkeletonRow key={index} />
                        ))}
                    </div>
                </Reveal>
                <Reveal open={!loading && rows.length > 0}>
                    <div role="rowgroup">
                        {rows.map((row) => (
                            <CapacityRow key={row.id} accountSlug={accountSlug} row={row} countsFailed={countsFailed} />
                        ))}
                    </div>
                </Reveal>
            </InlineDataTable>
            <Reveal open={error}>
                <p className="capacity-table-note">
                    {t('capacity.list.error')}{' '}
                    <button type="button" className="capacity-text-action" onClick={onRetry}>
                        {t('capacity.list.retry')}
                    </button>
                </p>
            </Reveal>
            <Reveal open={countsFailed && rows.length > 0}>
                <p className="capacity-table-note">
                    {t('capacity.list.countsFailed')}{' '}
                    <button type="button" className="capacity-text-action" onClick={onRetryCounts}>
                        {t('capacity.list.retry')}
                    </button>
                </p>
            </Reveal>
            <Reveal open={!loading && !error && empty}>
                <p className="capacity-table-note">{t('capacity.list.empty')}</p>
            </Reveal>
            <Reveal open={noMatch}>
                <p className="capacity-table-note">
                    {t('capacity.list.noMatch', { query: quote(query.trim()) })}{' '}
                    <button type="button" className="capacity-text-action" onClick={onClearSearch}>
                        {t('capacity.list.clearSearch')}
                    </button>
                </p>
            </Reveal>
        </div>
    )
}
